import Utils from "../basic/Utils";

/*
 * DitapPopup/DitapPanel을 관리하는 클래스.<br>
 * The class that manages the DitapPopup/DitapPanel.
 * @alias DitapPaneManager
 *
 * @constructor
 */
class DitapPaneManager {
  constructor() {
    this._panes = [];
    this._isMobile = Utils.isBodyWidthLessThan600();

    this._currentPane = undefined;
    this._currentIndex = undefined;
    this.createResizeEvent();
  }

  /**
   * DitapPopup/DitapPanel이 저장될 배열.<br>
   * Array to store DitapPopup/DitapPanel.
   * @type {object}
   */
  set panes(panes) {
    this._panes = panes;
  }
  get panes() {
    return this._panes;
  }

  /*
   * 모바일 여부.<br>
   * Whether it is mobile.
   */
  set isMobile(isMobile) {
    this._isMobile = isMobile;
  }
  get isMobile() {
    return this._isMobile;
  }

  /**
   * 현재 열린 창.<br>
   * The currently open pane.
   */
  set currentPane(currentPane) {
    this._currentPane = currentPane;
  }
  get currentPane() {
    return this._currentPane;
  }

  /*
   * 현재 열린 창의 인덱스.<br>
   * Index of the currently open pane.
   */
  set currentIndex(currentIndex) {
    this._currentIndex = currentIndex;
  }
  get currentIndex() {
    return this._currentIndex;
  }

  /**
   * 이 객체의 모든 리소스 제거.</br>
   * Remove all resources of this object.
   */
  destroy() {
    this.panes = [];
    this.isMobile = undefined;
    this.currentPane = undefined;
    this.currentIndex = undefined;
    if (this.resizeEvent) {
      window.removeEventListener("resize", this.resizeEvent);
    }
  }

  /*
   * 윈도우 크기 조절 시 이벤트 생성.</br>
   * Create an event for window resize.
   */
  createResizeEvent() {
    window.addEventListener(
      "resize",
      (this.resizeEvent = () => {
        const isMobile = Utils.isBodyWidthLessThan600();
        if (this.isMobile !== isMobile) {
          if (isMobile) {
            if (this.panes.length > 1) {
              this.currentIndex = this.panes.length - 1;
              this.closeAllPanes();
              this.showPane();
            }
          } else {
            this.openAllPanes();
          }
        }
        this.isMobile = isMobile;
      })
    );
  }

  /*
   * DitapPaneManager에 저장된 모든 창 닫기.<br>
   * Close all panes stored in DitapPaneManager.
   */
  closeAllPanes() {
    const panes = this.panes;
    for (let i = 0; i < panes.length; i++) {
      panes[i].classList.add("hide");
    }
  }

  /*
   * DitapPaneManager에 저장된 모든 창 열기.<br>
   * Open all panes stored in DitapPaneManager.
   */
  openAllPanes() {
    const panes = this.panes;
    for (let i = 0; i < panes.length; i++) {
      const directionButtons = panes[i].querySelectorAll(
        ".ditap-direction-btn"
      );

      if (directionButtons.length > 0) {
        directionButtons.forEach((container) => {
          container.classList.add("disabled");
          container.disabled = true;
        });
      }

      // const buttonContainer = panes[i].querySelector(".ditap-popup-button");
      // buttonContainer?.classList.remove("on");
      panes[i].classList.remove("hide");

      if (panes[i].classList.contains("ditap-panel-property")) {
        const panelContainer = document.getElementById("ditapPanel");
        panelContainer?.classList.remove("hide");
      }
    }
  }

  /*
   * 해당 창 닫기.<br>
   * Close the corresponding pane.
   * @param {object} pane 닫기할 창.<br>Pane to close.
   */
  closePane(pane) {
    pane.classList.add("hide");
  }

  /*
   * 이전 창 열기.<br>
   * Open the previous pane.
   */
  prevPane() {
    const panes = this.panes;
    const prevIndex = this.currentIndex;

    if (this.currentIndex - 1 >= 0) {
      this.currentIndex--;
    } else {
      this.currentIndex = panes.length - 1;
    }

    if (prevIndex !== this.currentIndex) {
      this.closePane(this.currentPane);
      this.showPane();
    }
  }

  /*
   * 다음 창 열기.<br>
   * Open the next pane.
   */
  nextPane() {
    const panes = this.panes;
    const prevIndex = this.currentIndex;

    if (this.currentIndex + 1 < panes.length) {
      this.currentIndex++;
    } else {
      this.currentIndex = 0;
    }
    if (prevIndex !== this.currentIndex) {
      this.closePane(this.currentPane);
      this.showPane();
    }
  }

  /*
   * DitapPaneManager에 창 추가.<br>
   * Add pane to DitapPaneManager.
   * @param {object} pane 추가할 창 요소.<br> Element to add as a pane.
   */
  addPane(pane) {
    const panes = this.panes;
    panes.push(pane);

    if (this.isMobile) {
      this.closeAllPanes();
      this.currentIndex = panes.length - 1;
      this.showPane();
    }
  }

  /*
   * DitapPaneManager에서 해당 창 제거.<br>
   * Remove the pane from the DitapPaneManager.
   * @param {object} pane 제거할 창 요소.<br> Element to remove as a pane.
   */
  removePane(pane) {
    const index = this.panes.indexOf(pane);
    if (index !== -1) {
      this.panes.splice(index, 1); // 배열에서 해당 창을 직접 제거

      if (this.isMobile && this.panes.length > 0) {
        if (index >= 0 && index <= this.panes.length - 1) {
          this.currentIndex = index;
        } else {
          this.currentIndex = 0;
        }
        this.showPane();
      }
    }
  }

  /*
   * 현재 인덱스에 해당하는 창 열기.<br>
   * Open the pane corresponding to the current index.
   */
  showPane() {
    if (this.currentIndex !== undefined) {
      const panes = this.panes;
      const currentPane = panes[this.currentIndex];
      this.currentPane = currentPane;

      const panelContainer = document.getElementById("ditapPanel");

      currentPane.classList.remove("hide");
      if (currentPane.classList.contains("ditap-panel-property")) {
        panelContainer?.classList.remove("hide");
      } else {
        panelContainer?.classList.add("hide");
      }

      panelContainer?.classList.remove("expanded");
      currentPane?.classList.remove("expanded");

      this.toggleDirectionButton();
    }
  }

  /*
   * 현재 창 방향 버튼 열고/닫기.<br>
   * Open/close the current window direction button.
   */
  toggleDirectionButton() {
    const panes = this.panes;
    const currentPane = this.currentPane;
    const directionButtons = currentPane.querySelectorAll(
      ".ditap-direction-btn"
    );
    if (directionButtons.length > 0) {
      if (panes.length <= 1) {
        directionButtons.forEach((container) => {
          container.classList.add("disabled");
          container.disabled = true;
        });
      } else {
        directionButtons.forEach((container) => {
          container.classList.remove("disabled");
          container.disabled = false;
        });
      }
    }
  }
}
export default DitapPaneManager;
